import React from "react";
import { Helmet } from "react-helmet";

const BASE_URL = "https://yoyokhel.com";
const DEFAULT_IMAGE = `${BASE_URL}/logo556.png`;
const SITE_NAME = "Yoyo Game";
const TWITTER_HANDLE = "@yoyogame";

/**
 * Reusable SEO head component.
 *
 * Props:
 *  title          – page title (will be suffixed with " • Yoyo Game")
 *  description    – meta description (max ~160 chars)
 *  keywords       – comma-separated keyword string
 *  image          – absolute URL to OG image (defaults to site logo)
 *  imageAlt       – alt text for OG image
 *  url            – canonical URL for this page
 *  type           – og:type (default: "website")
 *  noIndex        – boolean, set true for private/auth pages
 *  jsonLd         – plain object or array of objects to inject as JSON-LD
 *  breadcrumbs    – array of {name, url} for BreadcrumbList schema
 *  publishedTime  – ISO date string for article published time (og:article)
 *  modifiedTime   – ISO date string for article modified time (og:article)
 *  author         – author name string (og:article)
 */
const SEOHead = ({
  title,
  description,
  keywords,
  image = DEFAULT_IMAGE,
  imageAlt,
  url,
  type = "website",
  noIndex = false,
  jsonLd,
  breadcrumbs,
  publishedTime,
  modifiedTime,
  author,
}) => {
  const fullTitle = title
    ? `${title} • ${SITE_NAME}`
    : `${SITE_NAME} • Download & Play Games on PC, Mobile, Console & VR`;
  const canonicalUrl = url ? url : BASE_URL;

  // Build breadcrumb JSON-LD if breadcrumbs array is provided
  const breadcrumbJsonLd = breadcrumbs && breadcrumbs.length > 0
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Home",
            "item": BASE_URL,
          },
          ...breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            "position": i + 2,
            "name": crumb.name,
            "item": crumb.url,
          })),
        ],
      }
    : null;

  // Combine all JSON-LD entries
  const allJsonLd = [
    ...(jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : []),
    ...(breadcrumbJsonLd ? [breadcrumbJsonLd] : []),
  ];

  return (
    <Helmet>
      {/* Basic */}
      <title>{fullTitle}</title>
      {description && <meta name="description" content={description} />}
      {keywords && <meta name="keywords" content={keywords} />}
      <meta name="robots" content={noIndex ? "noindex, nofollow" : "index, follow"} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={fullTitle} />
      {description && <meta property="og:description" content={description} />}
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      {imageAlt && <meta property="og:image:alt" content={imageAlt} />}
      <meta property="og:locale" content="en_US" />

      {/* Article-specific OG tags */}
      {type === "article" && publishedTime && (
        <meta property="article:published_time" content={publishedTime} />
      )}
      {type === "article" && modifiedTime && (
        <meta property="article:modified_time" content={modifiedTime} />
      )}
      {type === "article" && author && (
        <meta property="article:author" content={author} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content={TWITTER_HANDLE} />
      <meta name="twitter:title" content={fullTitle} />
      {description && <meta name="twitter:description" content={description} />}
      <meta name="twitter:image" content={image} />
      {imageAlt && <meta name="twitter:image:alt" content={imageAlt} />}

      {/* JSON-LD structured data */}
      {allJsonLd.length > 0 && (
        <script type="application/ld+json">
          {JSON.stringify(allJsonLd)}
        </script>
      )}
    </Helmet>
  );
};

export default SEOHead;
